import React, { type Ref } from 'react';

import {
    calcInputRangeProgress,
    type InputRangeChange,
    type InputRangeFocus,
    normalizeInputRangeValue,
} from '@pkg/primitives';
import { clamp } from '@pkg/utils';

import { type FieldRangeHandler, type FieldRangeSize, type InputRangeSubset } from './FieldRange.types';

import { FakeThumb, FakeTrack, Output, PrimitiveRange, Progress, StyledFieldRange } from './FieldRange.style';

export interface FieldRangeProps extends InputRangeSubset {
    inputRef?: Ref<HTMLInputElement>;
    size?: FieldRangeSize;
    onFocus?: FieldRangeHandler;
    onBlur?: FieldRangeHandler;
    onChange?: FieldRangeHandler;
}

export function FieldRange({
    inputRef,
    size = 'slim',
    onFocus,
    onBlur,
    onChange,
    // Unadulterated `InputRange` props.
    id,
    value = 0,
    min = 0,
    max = 100,
    disabled = false,
    error = false,
    ...inputProps
}: FieldRangeProps) {
    const clampedValue = clamp(min, value, max);
    const progress = calcInputRangeProgress(clampedValue, { min, max });

    const hasError = max < min || error;
    const outputMarkup = id?.length ? <Output id={`${id}-Output`}>{clampedValue}</Output> : null;

    const handleBlur: InputRangeFocus | undefined = onBlur
        ? ({ target }) => onBlur(normalizeInputRangeValue(target.value, clampedValue), id)
        : undefined;

    const handleFocus: InputRangeFocus | undefined = onFocus
        ? ({ target }) => onFocus(normalizeInputRangeValue(target.value, clampedValue), id)
        : undefined;

    const handleChange: InputRangeChange | undefined = onChange
        ? ({ target }) => onChange(normalizeInputRangeValue(target.value, clampedValue), id)
        : undefined;

    return (
        <StyledFieldRange $disabled={disabled} $error={hasError} $progress={progress} $size={size}>
            <FakeTrack $disabled={disabled}>
                <Progress>
                    <FakeThumb $disabled={disabled} />
                    {outputMarkup}
                </Progress>
            </FakeTrack>

            <PrimitiveRange
                {...inputProps}
                ref={inputRef}
                id={id}
                value={clampedValue}
                min={min}
                max={max}
                disabled={disabled}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onChange={handleChange}
            />
        </StyledFieldRange>
    );
}
